import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, rootEffectsInit } from '@ngrx/effects';
import { concatMap, delay, first, map, switchMap, tap } from 'rxjs/operators';
import {
  addPerson,
  calculateOfferSuccess,
  changeContractDuration,
  changeOfferAddress,
  removeOfferForPerson,
  removePerson,
  selectFrameworkContractOnSummaryPage,
} from './offer.actions';
import { OffersService } from '../../api';
import { Store } from '@ngrx/store';
import {
  getAddress,
  getContractDuration,
  getFrameworkContract,
  getPersons,
  getSaleOfferBegin,
  getTotalPrice,
  hasPersons,
} from './offer.selector';
import { mapToOfferRequest } from './offer.mapper';
import { changeBasicInsuranceForPerson, removeBasicInsuranceForPerson } from './basic-insurance/actions';
import { catchError, filter, merge, pipe, withLatestFrom } from 'rxjs';
import {
  deselectAccidentDeathCapitalProduct,
  deselectAccidentDisabilityCapitalProduct,
  deselectAccidentMedicalExpensesSupplementProduct,
  deselectBasicProduct,
  deselectComplementaryProduct,
  deselectComplementaryVivaProduct,
  deselectDailyCashProduct,
  deselectDentalProduct,
  deselectEmergencyCareProduct,
  deselectHospitalCapitalProduct,
  deselectHospitalDailyCashProduct,
  deselectHospitalProduct,
  deselectHospitalVivaProduct,
  deselectLegalProtectionProduct,
  deselectOutpatientProduct,
  deselectOutpatientComfortProduct,
  deselectOutpatientVivaProduct,
  deselectSicknessDeathOrDisabilityCapitalProduct,
  selectAccidentDeathCapitalProduct,
  selectAccidentDisabilityCapitalProduct,
  selectAccidentMedicalExpensesSupplementProduct,
  selectBasicProduct,
  selectComplementaryProduct,
  selectComplementaryVivaProduct,
  selectDailyCashProduct,
  selectDentalProduct,
  selectEmergencyCareProduct,
  selectHospitalCapitalProduct,
  selectHospitalDailyCashProduct,
  selectHospitalProduct,
  selectHospitalVivaProduct,
  selectLegalProtectionProduct,
  selectOutpatientProduct,
  selectOutpatientComfortProduct,
  selectOutpatientVivaProduct,
  selectSicknessDeathOrDisabilityCapitalProduct,
} from './supplementary-insurance/actions';
import { TrackingLegacyService } from '../../services/tracking/legacy/tracking-legacy.service';
import { routerNavigatedAction } from '@ngrx/router-store';

export const offerCalculationActions = pipe(
  ofType(
    removeOfferForPerson,
    changeBasicInsuranceForPerson,
    removeBasicInsuranceForPerson,
    addPerson,
    removePerson,
    selectBasicProduct,
    deselectBasicProduct,
    selectHospitalProduct,
    deselectHospitalProduct,
    selectHospitalVivaProduct,
    deselectHospitalVivaProduct,
    selectOutpatientProduct,
    selectOutpatientVivaProduct,
    deselectOutpatientVivaProduct,
    deselectOutpatientProduct,
    selectComplementaryProduct,
    deselectComplementaryProduct,
    selectComplementaryVivaProduct,
    deselectComplementaryVivaProduct,
    selectAccidentDisabilityCapitalProduct,
    deselectAccidentDisabilityCapitalProduct,
    selectDailyCashProduct,
    deselectDailyCashProduct,
    selectAccidentDeathCapitalProduct,
    deselectAccidentDeathCapitalProduct,
    selectSicknessDeathOrDisabilityCapitalProduct,
    deselectSicknessDeathOrDisabilityCapitalProduct,
    selectDentalProduct,
    deselectDentalProduct,
    selectHospitalCapitalProduct,
    deselectHospitalCapitalProduct,
    selectHospitalDailyCashProduct,
    deselectHospitalDailyCashProduct,
    selectLegalProtectionProduct,
    deselectLegalProtectionProduct,
    changeContractDuration,
    changeOfferAddress,
    selectFrameworkContractOnSummaryPage,
    selectOutpatientComfortProduct,
    deselectOutpatientComfortProduct,
    selectEmergencyCareProduct,
    deselectEmergencyCareProduct,
    selectAccidentMedicalExpensesSupplementProduct,
    deselectAccidentMedicalExpensesSupplementProduct,
  ),
);

@Injectable()
export class OfferEffects {
  public calculateOffer$ = createEffect(() =>
    merge(
      this.actions$.pipe(offerCalculationActions),
      this.actions$.pipe(
        ofType(rootEffectsInit, routerNavigatedAction),
        withLatestFrom(this.store.select(getTotalPrice), this.store.select(hasPersons)),
        filter(([action, totalPrice, hasAtLeastOnePerson]) => hasAtLeastOnePerson && !totalPrice),
        // rootEffectsInit triggers before application is ready to do http requests
        delay(100),
      ),
    ).pipe(
      concatMap(() =>
        this.store
          .select(getPersons)
          .pipe(
            first(),
            withLatestFrom(
              this.store.select(getAddress),
              this.store.select(getSaleOfferBegin),
              this.store.select(getContractDuration),
              this.store.select(getFrameworkContract),
            ),
          ),
      ),
      switchMap(([persons, address, saleOfferBegin, contractDuration, frameworkContract]) => {
        const req = mapToOfferRequest(persons, address, saleOfferBegin, contractDuration, frameworkContract);
        return this.offersService.calculateOffer(req).pipe(
          catchError((e) => {
            throw new Error('error calculating offer: ' + JSON.stringify(e));
          }),
          tap((offer) => this.trackingLegacyService.trackOfferUpdate(persons, offer.total_price)),
          map((offer) =>
            calculateOfferSuccess({
              persons: offer.person_offers.map((person) => ({
                basicInsurancePrice: person.basic_insurance_price,
                supplementaryInsurancePrices: person.supplementary_insurance_prices,
                totalPrice: person.total_price,
              })),
              totalPrice: offer.total_price,
            }),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private offersService: OffersService,
    private trackingLegacyService: TrackingLegacyService,
  ) {}
}
